import React, { Children, useMemo } from 'react'
import type { ReactNode } from 'react'

import type { ListOfAvailability } from '@s4l/stayforlong/booking/ListOfAvailability'
import type { ID } from '@s4l/stayforlong/kernel/ID'
import { withErrorBoundary } from '@s4l/utils/hocs/withErrorBoundary'
import { findOneByName } from '@s4l/utils/library/react'

import { RoomConfiguratorContext } from './context'
import RoomConfiguratorForm from './Form'
import RoomConfiguratorHeader from './Header'
import Placeholder from './Placeholder'

import styles from './styles.module.css'

interface IRoomConfiguratorProps {
  children: ReactNode
  roomID: ID
  listOfAvailability: ListOfAvailability
  isMultiBooking?: boolean
}

function RoomConfigurator(props: Readonly<IRoomConfiguratorProps>) {
  const { children, listOfAvailability, roomID, isMultiBooking = false } = props

  const providerValue = useMemo(
    () => ({ listOfAvailability, roomID, isMultiBooking }),
    [listOfAvailability, roomID, isMultiBooking]
  )

  if (!roomID || listOfAvailability.isEmpty()) {
    console.error('RoomConfigurator: roomID and listOfAvailability are required')
    return null
  }

  const childrenCount = Children.count(children)

  if (childrenCount < 1 || childrenCount > 2) {
    console.error('RoomConfigurator: must have between 1 and 2 children')
    return null
  }

  const headerChildren: ReactNode | null = findOneByName(children, 'RoomConfigurator.Header')
  const formChildren: ReactNode | null = findOneByName(children, 'RoomConfigurator.Form')

  if (!formChildren) {
    console.error('RoomConfigurator: <RoomConfigurator.Form /> is required')
    return null
  }

  return (
    <RoomConfiguratorContext.Provider value={providerValue}>
      <div className={styles.roomConfiguratorContainer} data-testid="room-offers-list">
        {headerChildren}
        {formChildren}
      </div>
    </RoomConfiguratorContext.Provider>
  )
}

RoomConfigurator.Placeholder = Placeholder
RoomConfigurator.Form = RoomConfiguratorForm
RoomConfigurator.Header = RoomConfiguratorHeader

RoomConfigurator.Form.displayName = 'RoomConfigurator.Form'
RoomConfigurator.Header.displayName = 'RoomConfigurator.Header'
RoomConfigurator.displayName = 'RoomConfigurator'

export default withErrorBoundary(RoomConfigurator)
